import { useState } from "react";
import {
  View,
  Text,
  Pressable,
  ActivityIndicator,
  Linking,
} from "react-native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRouter } from "expo-router";
import styled from "styled-components/native";
import {
  signInWithEmailAndPassword,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/lib/firebase";
import { colors, radius, font } from "@/lib/theme";
import StumpsIcon from "@/components/StumpsIcon";

const MAX_WIDTH = 420;

/* ── Styled Components ── */

const SafeArea = styled(SafeAreaView)`
  flex: 1;
  background-color: ${colors.white};
`;

const Center = styled.View`
  flex: 1;
  align-items: center;
`;

const Content = styled.View`
  flex: 1;
  width: 100%;
  max-width: ${MAX_WIDTH}px;
  padding-horizontal: 24px;
`;

const IconCircle = styled.View`
  width: 64px;
  height: 64px;
  border-radius: 32px;
  background-color: ${colors.brand50};
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
`;

const LogoRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const LogoText = styled.Text`
  font-size: 28px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  letter-spacing: -0.5px;
`;

const Title = styled.Text`
  font-size: 24px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  letter-spacing: -0.5px;
  margin-bottom: 8px;
`;

const Subtitle = styled.Text`
  font-size: 15px;
  color: ${colors.inkSecondary};
  line-height: 22px;
  margin-bottom: 32px;
  text-align: center;
`;

const Label = styled.Text<{ extraMarginTop?: boolean }>`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  ${({ extraMarginTop }) => extraMarginTop ? "margin-top: 12px;" : ""}
`;

const Input = styled.TextInput`
  background-color: ${colors.bg};
  border-width: 2px;
  border-color: ${colors.line};
  border-radius: ${radius.card}px;
  padding-horizontal: 16px;
  padding-vertical: 16px;
  font-size: 16px;
  color: ${colors.ink};
  margin-bottom: 4px;
`;

const ErrorBox = styled.View`
  background-color: ${colors.dangerLight};
  border-radius: ${radius.md}px;
  padding-horizontal: 16px;
  padding-vertical: 12px;
  margin-top: 12px;
  margin-bottom: 4px;
`;

const ErrorText = styled.Text`
  color: ${colors.danger};
  font-size: 14px;
  font-weight: ${font.semibold};
`;

const PrimaryBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const SecondaryBtnText = styled.Text`
  color: ${colors.inkSecondary};
  font-size: 16px;
  font-weight: ${font.bold};
`;

const ToggleText = styled.Text`
  font-size: 14px;
  color: ${colors.brand};
  font-weight: ${font.bold};
`;

const Hint = styled.Text`
  font-size: 13px;
  color: ${colors.inkMuted};
  text-align: center;
  line-height: 18px;
  margin-top: 12px;
`;

const BodyCenter = styled.View`
  padding-top: 60px;
`;

const ToggleWrapper = styled.View`
  margin-top: 32px;
  align-items: center;
`;

const EmojiText = styled.Text`
  font-size: 28px;
`;

const BoldEmail = styled.Text`
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const NewUserText = styled.Text`
  font-size: 14px;
  color: ${colors.inkSecondary};
`;

const FooterLinks = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding-bottom: 24px;
`;

const FooterLink = styled(Pressable)``;

const FooterLinkText = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
`;

const FooterDot = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
`;

const HelpLinkText = styled.Text`
  font-size: 13px;
  color: ${colors.brand};
  font-weight: ${font.medium};
  padding-bottom: 12px;
`;

/* ── Component ── */

export default function LoginScreen() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"magic" | "password">("magic");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleMagicLink = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setError("");
    try {
      const sendMagicLink = httpsCallable(functions, "sendMagicLink");
      await sendMagicLink({ email, type: "signin" });
      setSent(true);
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handlePassword = async () => {
    if (!email.trim() || !password.trim()) return;
    setLoading(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Auth state change in AuthContext will redirect
    } catch (err: any) {
      if (err.code === "auth/invalid-credential" || err.code === "auth/wrong-password") {
        setError("Wrong email or password.");
      } else if (err.code === "auth/user-not-found") {
        setError("No account found with this email.");
      } else {
        setError(err.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <SafeArea edges={["top"]}>
        <Center>
          <Content style={{ justifyContent: "center", alignItems: "center" }}>
            <IconCircle>
              <EmojiText>✉️</EmojiText>
            </IconCircle>
            <Title style={{ textAlign: "center" }}>Check your email</Title>
            <Subtitle>
              We sent a sign-in link to{" "}
              <BoldEmail>{email}</BoldEmail>
              .{"\n"}Tap the link to continue.
            </Subtitle>
          </Content>
          <Pressable
            style={{ paddingBottom: 32, alignItems: "center" }}
            onPress={() => setSent(false)}
          >
            <ToggleText>Use a different email</ToggleText>
          </Pressable>
        </Center>
      </SafeArea>
    );
  }

  return (
    <SafeArea edges={["top"]}>
      <KeyboardAwareScroll
        contentContainerStyle={{ flexGrow: 1 }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <Center>
          <View style={{ width: "100%", maxWidth: MAX_WIDTH, paddingHorizontal: 24 }}>
            <BodyCenter>
              {/* Header */}
              <LogoRow>
                <StumpsIcon size={36} />
                <LogoText>Sign in</LogoText>
              </LogoRow>
              <Subtitle>Your club, finally in one app.</Subtitle>

            {/* Email */}
            <Label>Email address</Label>
            <Input
              placeholder="you@cricketclub.com"
              placeholderTextColor={colors.inkMuted}
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
            />

            {/* Password field (only in password mode) */}
            {mode === "password" && (
              <>
                <Label extraMarginTop>Password</Label>
                <Input
                  placeholder="Enter your password"
                  placeholderTextColor={colors.inkMuted}
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry
                />
              </>
            )}

            {error ? (
              <ErrorBox>
                <ErrorText>{error}</ErrorText>
              </ErrorBox>
            ) : null}

            {/* Primary button */}
            <Pressable
              style={({ pressed }) => [
                {
                  backgroundColor: colors.brand,
                  borderRadius: radius.card,
                  paddingVertical: 16,
                  alignItems: "center" as const,
                  marginTop: 16,
                  shadowColor: colors.brand,
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.25,
                  shadowRadius: 14,
                  elevation: 4,
                },
                pressed && { opacity: 0.9 },
              ]}
              onPress={mode === "magic" ? handleMagicLink : handlePassword}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <PrimaryBtnText>
                  {mode === "magic" ? "Send magic link →" : "Sign in"}
                </PrimaryBtnText>
              )}
            </Pressable>

            {mode === "magic" && (
              <Hint>
                We'll email you a link to sign in — no password needed.
              </Hint>
            )}

            {/* Toggle between modes */}
            <Pressable
              onPress={() => {
                setMode(mode === "magic" ? "password" : "magic");
                setError("");
              }}
            >
              <ToggleWrapper>
                <ToggleText>
                  {mode === "magic" ? "Sign in with password instead" : "Sign in with magic link instead"}
                </ToggleText>
              </ToggleWrapper>
            </Pressable>

            </BodyCenter>
          </View>

          <View style={{ flex: 1 }} />

          {/* Register club card */}
        <Pressable
          onPress={() => router.push("/(auth)/signup-club")}
          style={({ pressed }) => [
            {
              marginHorizontal: 24,
              marginBottom: 16,
              backgroundColor: colors.brand50,
              borderRadius: radius.card,
              borderWidth: 1,
              borderColor: colors.brandLight,
              padding: 18,
              maxWidth: MAX_WIDTH - 48,
              alignSelf: "center",
              width: "100%",
            },
            pressed && { opacity: 0.8 },
          ]}
        >
          <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 10 }}>
            <Text style={{ fontSize: 22, marginRight: 10 }}>🏏</Text>
            <Text style={{ fontSize: 16, fontWeight: "800", color: colors.ink }}>
              Register your club
            </Text>
            <Text style={{ color: colors.brand, fontSize: 16, fontWeight: "700", marginLeft: "auto" }}>→</Text>
          </View>
          <Text style={{ fontSize: 13, color: colors.inkSecondary, lineHeight: 18, marginBottom: 12 }}>
            Free to get started. Set up in minutes.
          </Text>
          <View style={{ borderTopWidth: 1, borderTopColor: colors.line, paddingTop: 10 }}>
            <Text style={{ fontSize: 11, color: colors.inkMuted, lineHeight: 16 }}>
              Waiting for an invite? Check your email or ask your club admin to resend it.
            </Text>
          </View>
        </Pressable>

          {/* Help & Legal links */}
          <FooterLink onPress={() => router.push("/(auth)/help")}>
            <HelpLinkText>Having trouble? Get help</HelpLinkText>
          </FooterLink>
          <FooterLinks>
            <FooterLink onPress={() => Linking.openURL("https://inthecrease.uk/privacy")}>
              <FooterLinkText>Privacy</FooterLinkText>
            </FooterLink>
            <FooterDot>·</FooterDot>
            <FooterLink onPress={() => Linking.openURL("https://inthecrease.uk/terms")}>
              <FooterLinkText>Terms</FooterLinkText>
            </FooterLink>
          </FooterLinks>
        </Center>
      </KeyboardAwareScroll>
    </SafeArea>
  );
}
